import { Add, Remove } from '@mui/icons-material';
import { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GarmentDto } from '../../api/types/LookDtos';
import { CartItemsContext } from '../../context/CartItemsContext';
import { SelectedGarmentContext } from '../../context/SelectedGarmentContext';
import { SelectedGarmentSizeContext } from '../../context/SelectedGarmentSizeContext';
import { CartItemsContextType } from '../../context/types/CartItemsContextType';
import { SelectedGarmentContextType } from '../../context/types/SelectedGarmentContentType';
import { SelectedGarmentSizeContextType } from '../../context/types/SelectedGarmentSizeContextType';
import StraightenIcon from '@mui/icons-material/Straighten';
import { TextLabel, TextHeader, TextRegular } from '../Text';
import { BuyGarmentButton } from '../Button';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import {
  MIN_PC_BODY_WIDTH,
  MIN_PC_S_BODY_WIDTH,
  MIN_TABLET_BODY_WIDTH,
} from '../../constants/StylistConstants';

interface TransferGarment {
  garment: GarmentDto;
}

const GarmentDetails = (transferGarment: TransferGarment) => {
  const [quantity, setQuantity] = useState(1);
  const [totalPrice, setTotalPrice] = useState(0);
  const { updateCartItems } = useContext(
    CartItemsContext,
  ) as CartItemsContextType;
  const { selectedGarmentSizeId, saveSelectedGarmentSizeId } = useContext(
    SelectedGarmentSizeContext,
  ) as SelectedGarmentSizeContextType;
  const { selectedGarmentId } = useContext(
    SelectedGarmentContext,
  ) as SelectedGarmentContextType;
  const garment: GarmentDto = transferGarment.garment;
  const garmentPrice: number = +garment.price;

  const garmentDescription: string[] = garment.description.split(';');

  const selectedSize = useMemo(() => {
    return garment.sizes[selectedGarmentSizeId[selectedGarmentId]];
  }, [garment, selectedGarmentId, selectedGarmentSizeId]);

  useEffect(() => {
    setTotalPrice(+garment.price);
    setQuantity(1);
  }, [garment]);

  const addQuantity = () => {
    setQuantity(quantity + 1);
    setTotalPrice(totalPrice + garmentPrice);
  };

  const removeQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
      setTotalPrice(totalPrice - garmentPrice);
    }
  };

  return garment ? (
    <Container>
      <ImagesWrapper>
        <MainView>
          <MainImg src={garment?.images[0]} />
        </MainView>
        <SideView>
          {garment?.images.map((image, index) => (
            <SideImg src={image} key={index} />
          ))}
        </SideView>
      </ImagesWrapper>
      <InfoWrapper>
        <TextLabel fontWeight={300}>NEW ARRIVALS</TextLabel>
        <NamePriceWrapper>
          <TextHeader
            fontWeight={500}
            style={{ width: '90%', wordBreak: 'break-word' }}
          >
            {garment.name}
          </TextHeader>
          <TextHeader fontWeight={500}>${garment.price}</TextHeader>
        </NamePriceWrapper>
        <SizeQuantityWrapper>
          <SizeWrapper>
            <TextLabel fontWeight={300}>choose size</TextLabel>
            <FilterSizeWrapper>
              <FilterSize
                onChange={(e) =>
                  saveSelectedGarmentSizeId(
                    selectedGarmentId,
                    e.target.selectedIndex,
                  )
                }
                value={selectedSize}
              >
                {garment.sizes.map((size, index) => (
                  <FilterSizeOption key={index}>{size}</FilterSizeOption>
                ))}
              </FilterSize>
            </FilterSizeWrapper>
          </SizeWrapper>
          <QuantityWrapper>
            <TextLabel fontWeight={300}>quantity</TextLabel>
            <QuantityContainer>
              <Remove
                onClick={() => removeQuantity()}
                style={{ cursor: 'pointer' }}
              />
              <Quantity>
                <TextRegular fontWeight={500}>{quantity}</TextRegular>
              </Quantity>
              <Add
                onClick={() => addQuantity()}
                style={{ cursor: 'pointer' }}
              />
            </QuantityContainer>
          </QuantityWrapper>
        </SizeQuantityWrapper>
        <SizeGuideWrapper>
          <StraightenIcon fontSize={'large'} />
          <TextLabel fontWeight={700}>size guide</TextLabel>
        </SizeGuideWrapper>
        <BuyWrapper>
          <BuyGarmentButton onClick={() => updateCartItems(quantity)}>
            <LocalMallIcon />
            add to bag
          </BuyGarmentButton>
          <TextHeader fontWeight={500}>${totalPrice}</TextHeader>
        </BuyWrapper>
        <DetailsWrapper>
          <TextLabel fontWeight={300}>more details</TextLabel>
          <TextLabel fontWeight={600}>
            {garmentDescription[4].split(':')[1]}
          </TextLabel>
          <TextLabel fontWeight={600}>
            {garmentDescription[0].replace(':', ': ')}
          </TextLabel>
          <TextLabel fontWeight={600}>
            {garmentDescription[1].replace(':', ': ')}
          </TextLabel>
          <TextLabel fontWeight={600}>
            {garmentDescription[2].replace(':', ': ')}
          </TextLabel>
        </DetailsWrapper>
      </InfoWrapper>
    </Container>
  ) : (
    <></>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  padding-bottom: 50px;

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    display: block;
  }
`;

const ImagesWrapper = styled.div`
  width: 533px;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    width: 456px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    width: 614px;
  }

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    width: 323px;
  }
`;

const MainView = styled.div`
  display: flex;
  width: 100%;
  height: 660px;
  align-items: center;
  overflow: hidden;
  border: 1px solid #b4b4b4;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    height: 565px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    height: 763px;
  }

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    height: 400px;
  }
`;

const MainImg = styled.img`
  margin: 10%;
  width: 80%;
  height: 80%;
`;

const SideView = styled.div`
  margin-top: 15px;
  height: 83px;
  gap: 10px;
  flex-direction: row;
  display: flex;
  overflow: hidden;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    height: 70px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    height: 95px;
    margin-top: 10px;
  }

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    height: 50px;
    margin-top: 5px;
  }
`;

const SideImg = styled.img`
  padding: 3px;
  width: 75px;
  height: 75px;
  cursor: pointer;
  border: 1px solid #b4b4b4;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    width: 62px;
    height: 62px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    width: 87px;
    height: 87px;
  }

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    width: 42px;
    height: 42px;
  }
`;

const InfoWrapper = styled.div`
  width: 640px;
  padding-left: 135px;
  padding-top: 25px;
  position: relative;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    width: 456px;
    padding-left: 24px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    width: 100%;
    padding-top: 50px;
    padding-left: 0;
  }

  @media (max-width: ${MIN_TABLET_BODY_WIDTH - 1}px) {
    width: 100%;
    padding-top: 50px;
    padding-left: 0;
  }
`;

const NamePriceWrapper = styled.div`
  padding-top: 15px;
  padding-bottom: 50px;
  display: grid;
  grid-template-columns: auto 0fr;
  align-items: center;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    padding-bottom: 40px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    padding-bottom: 35px;
    padding-top: 10px;
  }
`;

const SizeQuantityWrapper = styled.div`
  display: flex;
  padding-bottom: 35px;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    padding-bottom: 25px;
  }
`;
const SizeWrapper = styled.div`
  display: grid;
  gap: 20px;
  padding-right: 100px;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    padding-right: 70px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    gap: 15px;
  }
`;

const QuantityWrapper = styled.div`
  display: grid;
  gap: 20px;

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    gap: 15px;
  }
`;

const FilterSizeWrapper = styled.div`
  position: relative;
  display: inline;
  border: 1px solid #000000;
  background-color: white;
  &:after {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    pointer-events: none;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    top: 20px;
    right: 0.75em;
    border-top: 8px solid black;
    opacity: 0.5;
    @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
      top: 15px;
    }
    @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
      top: 15px;
    }
  }
`;

const FilterSize = styled.select`
  -webkit-appearance: none;
  background-color: white;
  padding-left: 20px;

  width: 120px;
  height: 50px;
  border: none;
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 30px;
  text-transform: lowercase;
  color: #000000;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    width: 85px;
    height: 35px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    font-size: 14.1615px;
    line-height: 21px;
  }
`;
const FilterSizeOption = styled.option``;

const QuantityContainer = styled.div`
  border: 1px solid #000000;
  display: flex;
  align-items: center;
  padding: 0 17px;
  height: 50px;
  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    height: 35px;
    padding: 0 8px;
  }
`;

const Quantity = styled.span`
  border: 1px solid #000000;
  width: 115px;
  height: 50px;
  margin: 0 17px;
  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    height: 35px;
    width: 80px;
    margin: 0 8px;
  }
  background: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SizeGuideWrapper = styled.div`
  width: 150px;
  text-decoration: underline;
  cursor: pointer;
  display: flex;
  gap: 20px;
  align-items: center;
`;

const BuyWrapper = styled.div`
  padding-top: 50px;
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    padding-top: 40px;
  }

  @media (max-width: ${MIN_PC_S_BODY_WIDTH - 1}px) {
    padding-top: 35px;
  }
`;

const DetailsWrapper = styled.div`
  display: grid;
  padding-top: 50px;
  gap: 20px;

  @media (max-width: ${MIN_PC_BODY_WIDTH - 1}px) {
    padding-top: 35px;
    gap: 15px;
  }
`;

export default GarmentDetails;
